import { ReactNode, useCallback, useState } from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  useReactTable,
  VisibilityState
} from '@tanstack/react-table';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { twMerge } from 'tailwind-merge';
import { EmployeeListItem } from '@pxtra-client-lib-legacy/gen';
import { SortableTableHeader } from '../shared/sortable-header';
import Pagination from '../shared/pagination';
import { DEFAULT_PAGE_SIZE, scrollableTableContainerSx } from '../shared/utils';
import { DataTableLoadingBar } from '../shared/loading-bar';
import { TableToolBar } from '../shared/toolbar';
import { globalFilterFn } from '../../../util/filter';
import EmptyTable from '../../../icons/svg/empty-table.svg';
import { useTranslation } from 'react-i18next';
import { Button } from '../../atomic/button';
import { themeConfig } from '../../../theme/themeConfig';

interface EmployeeGroupTableProps {
  data: EmployeeListItem[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<EmployeeListItem, any>[];
  isLoading?: boolean;
  isFetching?: boolean;
  headline?: string;
  additionalToolbarComponent?: ReactNode;
  onRowDoubleClick?: (employee: EmployeeListItem) => void;
  getRowClassName?: (employee: EmployeeListItem) => string;
  columnVisibility?: VisibilityState;
  pageSize?: number;
  className?: string;
  enableRowSelection?: boolean;
  bulkActionLabel?: string;
  onBulkAction?: (selectedIds: string[]) => void;
}

export const EmployeeGroupTable = ({
  data,
  columns,
  isLoading = false,
  isFetching = false,
  headline,
  additionalToolbarComponent,
  onRowDoubleClick,
  getRowClassName,
  columnVisibility = {},
  pageSize = DEFAULT_PAGE_SIZE,
  className,
  enableRowSelection = false,
  bulkActionLabel,
  onBulkAction
}: EmployeeGroupTableProps) => {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const table = useReactTable({
    data,
    columns,
    enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    globalFilterFn: globalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    autoResetPageIndex: false,
    getRowId: (row) => row.id ?? 'MISSING_ROW_ID',
    state: {
      pagination,
      globalFilter,
      columnFilters,
      rowSelection
    },
    initialState: {
      columnVisibility
    }
  });

  const selectedCount = Object.keys(rowSelection).length;

  const handleBulkAction = useCallback(() => {
    if (!onBulkAction) return;
    const selectedIds = table.getSelectedRowModel().rows.map((row) => row.original.id ?? '');
    onBulkAction(selectedIds);
    setRowSelection({});
  }, [onBulkAction, table]);

  return (
    <div className={twMerge('overflow-hidden flex flex-col', className)}>
      <div className="flex items-center min-h-12 justify-between">
        {headline && <span className="text-base font-bold text-primary-main">{headline}</span>}
        <div className="flex items-center gap-2">
          {onBulkAction && selectedCount > 0 && (
            <Button variant="primary" size="small" className="mr-4" onClick={handleBulkAction}>
              {bulkActionLabel ?? t('template:ADD')} ({selectedCount})
            </Button>
          )}
          {additionalToolbarComponent}
        </div>
      </div>
      <TableToolBar table={table} />
      <TableContainer sx={scrollableTableContainerSx('calc(100vh - 400px)')}>
        <Table stickyHeader className="bg-white">
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <SortableTableHeader
                    key={header.id}
                    header={header}
                    isLoading={isLoading || isFetching}
                    hasData={data.length > 0}
                  />
                ))}
              </TableRow>
            ))}
            <TableRow>
              <TableCell
                sx={{
                  padding: 0,
                  borderBottom: 'none',
                  backgroundColor: 'transparent'
                }}
                colSpan={columns.length}
              >
                <DataTableLoadingBar isLoading={isLoading || isFetching} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="!border-none">
                  <div className="flex flex-col items-center justify-center">
                    <img src={EmptyTable} alt={t('common:NO_DATA')} className="w-64 h-64" />
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                const rowClassName = getRowClassName?.(row.original) ?? '';
                return (
                  <TableRow
                    key={row.id}
                    onDoubleClick={() => onRowDoubleClick?.(row.original)}
                    sx={{
                      cursor: onRowDoubleClick ? 'pointer' : 'default',
                      ...(rowClassName === 'newRow' && {
                        backgroundColor: themeConfig.colors.heaven[200]
                      }),
                      ...(rowClassName === 'oldRow' && {
                        backgroundColor: themeConfig.colors.error.lightest
                      })
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell className="py-2 border-grey-100" key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div className="mt-auto">
        <Pagination table={table} />
      </div>
    </div>
  );
};
